import { useMemo, useState, type ReactNode } from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/cn'

export type ComboboxOption<V extends string | number = string> = {
  value: V
  label: string
  description?: string
  disabled?: boolean
  /** Optional leading icon (e.g. country flag). Rendered before the label
   *  both in the trigger (for the selected row) and in every list item. */
  icon?: ReactNode
}

export interface ComboboxProps<V extends string | number = string> {
  value: V | null
  onChange: (value: V | null) => void
  options: ComboboxOption<V>[]
  placeholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  /**
   * Cap displayed results - keeps DOM cheap on very long lists. The default
   * is generous (10k) because the most common use is a tenant's full chart
   * of accounts, taxonomy, or vendor list; virtualisation isn't wired up,
   * but the `<ScrollArea>` keeps the popover bounded so even 2–3k entries
   * scroll fine. Callers with truly huge lists can lower this.
   */
  maxResults?: number
  triggerClassName?: string
  /** Override the popover width. Defaults to trigger width with a 320 px floor. */
  popoverClassName?: string
  /**
   * Fires when the popover opens - callers can pass a `refetch` from a
   * TanStack Query hook so the list is freshly pulled from the server the
   * moment the user clicks the trigger (another tenant member may have
   * added/renamed entries since the page was mounted).
   */
  onOpen?: () => void
}

/**
 * Searchable single-select - replaces frontend/public/js/searchable-dropdown.js.
 * Built on Radix Popover so the listbox renders to <body> (avoids overflow
 * clipping inside cards/modals), with a plain text filter and keyboard-
 * friendly select/clear.
 */
export function Combobox<V extends string | number = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyMessage = 'No matches.',
  disabled,
  className,
  maxResults = 10000,
  triggerClassName,
  popoverClassName,
  onOpen,
}: ComboboxProps<V>) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) {
      onOpen?.()
    } else {
      setQuery('')
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = q
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            (o.description?.toLowerCase().includes(q) ?? false) ||
            String(o.value).toLowerCase().includes(q),
        )
      : options
    return list.slice(0, maxResults)
  }, [options, query, maxResults])

  const current = options.find((o) => o.value === value) ?? null

  return (
    <div className={cn('w-full', className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled}
            aria-expanded={open}
            className={cn('w-full justify-between font-normal', triggerClassName)}
          >
            <span className={cn('flex min-w-0 items-center gap-2', !current && 'text-muted-foreground')}>
              {current?.icon ? <span className="shrink-0">{current.icon}</span> : null}
              <span className="truncate">{current ? current.label : placeholder}</span>
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            // Default width: match the trigger, with a modest floor so narrow
            // grid cells still get a readable dropdown, and a cap at 90 vw so
            // the popover never spills off a small viewport. We keep the
            // floor narrow (260px) so wide triggers don't stretch the list
            // and leave empty space on the right. Callers that want a
            // specific width can override via `popoverClassName`.
            'w-[--radix-popover-trigger-width] min-w-[min(260px,90vw)] max-w-[90vw] p-0',
            popoverClassName,
          )}
          align="start"
        >
          <div className="flex items-center gap-2 border-b p-2">
            <Search className="size-4 opacity-60" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder="Search…"
              className="h-8 border-0 bg-transparent p-0 focus-visible:ring-0"
            />
          </div>
          {/* Use a plain overflow-y-auto div instead of Radix ScrollArea.
              Radix's viewport is `h-full w-full`, so a `max-h` wrapper
              never produces a scrollable area - the viewport has no
              definite height to grow into. A native scroll container is
              more reliable and lets the list stay short when there are
              just a few matches.

              stopPropagation on wheel/touchmove: when this Combobox lives
              inside a Radix Dialog, Dialog's overlay swallows wheel
              events past its own viewport bounds to prevent background-
              page scrolling. The popover renders to body so it sits
              "outside" the dialog tree, but the wheel event still
              bubbles through React's synthetic event system back into
              the dialog. Stopping propagation here keeps the wheel
              scroll inside this list. Click-and-drag on the scrollbar
              works because it doesn't fire `wheel`. */}
          <div
            className="max-h-[min(60vh,420px)] overflow-y-auto overscroll-contain"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              <ul role="listbox" className="py-1">
                {filtered.map((opt) => {
                  const active = opt.value === value
                  return (
                    <li key={String(opt.value)}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        disabled={opt.disabled}
                        onClick={() => {
                          onChange(opt.value)
                          setOpen(false)
                          setQuery('')
                        }}
                        className={cn(
                          'flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-40',
                          active && 'bg-accent text-accent-foreground',
                        )}
                      >
                        <Check className={cn('mt-0.5 h-4 w-4 shrink-0', active ? 'opacity-100' : 'opacity-0')} />
                        {opt.icon ? <span className="mt-0.5 shrink-0">{opt.icon}</span> : null}
                        <span className="min-w-0 flex-1">
                          <span className="block whitespace-normal break-words font-medium">
                            {opt.label}
                          </span>
                          {opt.description ? (
                            <span className="block whitespace-normal break-words text-xs text-muted-foreground">
                              {opt.description}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
